import PlayIcon from "@mui/icons-material/PlayArrow";
import ScheduleIcon from "@mui/icons-material/Schedule";
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { addDays, differenceInDays, differenceInHours, differenceInMinutes } from "date-fns";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TabPanel } from "../../../components/TabPanel";
import { ParkingConfigForm } from "../TemporalTest/components/ParkingConfigForm";
import { AutoParkDialogProps } from "./types";
import { TripList } from "./components/TripList";

export const AutoParkDialog: React.FC<AutoParkDialogProps> = ({
  open,
  onClose,
  config,
  onConfigChange,
  onRunAutoPark,
  isScheduledView = false,
  timeRange,
  onTimeRangeChange,
  tripCount = 0,
  groupedTrips = [],
  selectedTripIds,
  onToggleTripSelection,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);

  // Reset to Time Range tab whenever dialog opens
  useEffect(() => {
    if (open) {
      setActiveTab(0);
    }
  }, [open]);

  // Calculate duration between start and end times
  const calculateDuration = (start: Date, end: Date) => {
    const days = differenceInDays(end, start);
    const hours = differenceInHours(end, start) % 24;
    const minutes = differenceInMinutes(end, start) % 60;

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Quick duration preset handlers
  const handleOneDayRange = () => {
    if (timeRange && onTimeRangeChange) {
      const newEnd = addDays(timeRange.start, 1);
      onTimeRangeChange(timeRange.start, newEnd);
    }
  };

  const handleThreeDayRange = () => {
    if (timeRange && onTimeRangeChange) {
      const newEnd = addDays(timeRange.start, 3);
      onTimeRangeChange(timeRange.start, newEnd);
    }
  };

  const handleSevenDayRange = () => {
    if (timeRange && onTimeRangeChange) {
      const newEnd = addDays(timeRange.start, 7);
      onTimeRangeChange(timeRange.start, newEnd);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('screens.ramp.autoPark.dialog.title')}</DialogTitle>
      <DialogContent sx={{ maxHeight: "70vh", overflowY: "auto" }}>
        {isScheduledView ? (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            >
              <Tab label={t('screens.ramp.autoPark.dialog.tabs.timeRange')} />
              <Tab label={t('screens.ramp.autoPark.dialog.tabs.algorithm')} />
            </Tabs>

            <TabPanel value={activeTab} index={0}>
              <Stack spacing={2}>
                {/* Date/Time Pickers Row */}
                <Stack direction="row" spacing={1.5} alignItems="flex-end" sx={{ width: "100%" }}>
                  <Stack spacing={1} sx={{ flexGrow: 1, flexShrink: 0, minWidth: 230 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('screens.ramp.autoPark.dialog.timeRange.startTime')}
                    </Typography>
                    <DateTimePicker
                      value={timeRange?.start || new Date()}
                      onChange={(newValue) => {
                        if (newValue && timeRange && onTimeRangeChange) {
                          onTimeRangeChange(newValue, timeRange.end);
                        }
                      }}
                      ampm={false}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                        },
                      }}
                    />
                  </Stack>

                  <Stack spacing={1} sx={{ flexGrow: 1, flexShrink: 0, minWidth: 230 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('screens.ramp.autoPark.dialog.timeRange.endTime')}
                    </Typography>
                    <DateTimePicker
                      value={timeRange?.end || new Date()}
                      onChange={(newValue) => {
                        if (newValue && timeRange && onTimeRangeChange) {
                          onTimeRangeChange(timeRange.start, newValue);
                        }
                      }}
                      ampm={false}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                        },
                      }}
                    />
                  </Stack>

                  <Stack spacing={1} sx={{ flexShrink: 0, width: 90 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" color="text.secondary" noWrap>
                        {t('screens.ramp.autoPark.dialog.timeRange.duration')}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      noWrap
                      sx={{ height: 40, display: "flex", alignItems: "center" }}
                    >
                      {timeRange ? calculateDuration(timeRange.start, timeRange.end) : "—"}
                    </Typography>
                  </Stack>
                </Stack>

                {/* Quick Duration Presets - Centered */}
                <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mt: -1 }}>
                  <ButtonGroup variant="outlined" size="small">
                    <Button onClick={handleOneDayRange}>{t('screens.ramp.autoPark.dialog.timeRange.presets.oneDay')}</Button>
                    <Button onClick={handleThreeDayRange}>{t('screens.ramp.autoPark.dialog.timeRange.presets.threeDays')}</Button>
                    <Button onClick={handleSevenDayRange}>{t('screens.ramp.autoPark.dialog.timeRange.presets.sevenDays')}</Button>
                  </ButtonGroup>
                </Stack>

                {/* Trip Selection Section */}
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('screens.ramp.autoPark.dialog.timeRange.tripSelection', {
                      selected: selectedTripIds?.size || 0,
                      total: tripCount
                    })}
                  </Typography>
                  <TripList
                    groupedTrips={groupedTrips}
                    selectedTripIds={selectedTripIds || new Set()}
                    onToggleTripSelection={onToggleTripSelection || (() => {})}
                  />
                </Stack>
              </Stack>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <ParkingConfigForm
                bufferMinutes={config.bufferMinutes}
                plannedMoveCap={config.plannedMoveCap}
                allowTrapping={config.allowTrapping}
                egressWeight={config.egressWeight}
                wasteWeight={config.wasteWeight}
                structuralWeight={config.structuralWeight}
                zonePriorityWeight={config.zonePriorityWeight}
                min_dwell_weight={config.min_dwell_weight}
                sortingStrategy={config.sortingStrategy}
                arrivalStagingDwellMinutes={config.arrivalStagingDwellMinutes}
                departureStagingDwellMinutes={config.departureStagingDwellMinutes}
                quickTurnThresholdMinutes={config.quickTurnThresholdMinutes}
                onBufferMinutesChange={(value) =>
                  onConfigChange({ bufferMinutes: value })
                }
                onPlannedMoveCapChange={(value) =>
                  onConfigChange({ plannedMoveCap: value })
                }
                onAllowTrappingChange={(value) =>
                  onConfigChange({ allowTrapping: value })
                }
                onEgressWeightChange={(value) =>
                  onConfigChange({ egressWeight: value })
                }
                onWasteWeightChange={(value) =>
                  onConfigChange({ wasteWeight: value })
                }
                onStructuralWeightChange={(value) =>
                  onConfigChange({ structuralWeight: value })
                }
                onZonePriorityWeightChange={(value) =>
                  onConfigChange({ zonePriorityWeight: value })
                }
                onMinDwellWeightChange={(value) =>
                  onConfigChange({ min_dwell_weight: value })
                }
                onSortingStrategyChange={(value) =>
                  onConfigChange({ sortingStrategy: value })
                }
                onArrivalStagingDwellMinutesChange={(value) =>
                  onConfigChange({ arrivalStagingDwellMinutes: value })
                }
                onDepartureStagingDwellMinutesChange={(value) =>
                  onConfigChange({ departureStagingDwellMinutes: value })
                }
                onQuickTurnThresholdMinutesChange={(value) =>
                  onConfigChange({ quickTurnThresholdMinutes: value })
                }
                size="small"
                layout="column"
                disabledFields={[]}
                disabledSortingOptions={[
                  "arrival",
                  "longest-dwell",
                  "latest-departure",
                  "hybrid",
                ]}
              />
            </TabPanel>
          </LocalizationProvider>
        ) : (
          <Box p={2}>
            <ParkingConfigForm
              bufferMinutes={config.bufferMinutes}
              plannedMoveCap={config.plannedMoveCap}
              allowTrapping={config.allowTrapping}
              egressWeight={config.egressWeight}
              wasteWeight={config.wasteWeight}
              structuralWeight={config.structuralWeight}
              zonePriorityWeight={config.zonePriorityWeight}
              min_dwell_weight={config.min_dwell_weight}
              sortingStrategy={config.sortingStrategy}
              arrivalStagingDwellMinutes={config.arrivalStagingDwellMinutes}
              departureStagingDwellMinutes={config.departureStagingDwellMinutes}
              quickTurnThresholdMinutes={config.quickTurnThresholdMinutes}
              onBufferMinutesChange={(value) =>
                onConfigChange({ bufferMinutes: value })
              }
              onPlannedMoveCapChange={(value) =>
                onConfigChange({ plannedMoveCap: value })
              }
              onAllowTrappingChange={(value) =>
                onConfigChange({ allowTrapping: value })
              }
              onEgressWeightChange={(value) =>
                onConfigChange({ egressWeight: value })
              }
              onWasteWeightChange={(value) =>
                onConfigChange({ wasteWeight: value })
              }
              onStructuralWeightChange={(value) =>
                onConfigChange({ structuralWeight: value })
              }
              onZonePriorityWeightChange={(value) =>
                onConfigChange({ zonePriorityWeight: value })
              }
              onMinDwellWeightChange={(value) =>
                onConfigChange({ min_dwell_weight: value })
              }
              onSortingStrategyChange={(value) =>
                onConfigChange({ sortingStrategy: value })
              }
              onArrivalStagingDwellMinutesChange={(value) =>
                onConfigChange({ arrivalStagingDwellMinutes: value })
              }
              onDepartureStagingDwellMinutesChange={(value) =>
                onConfigChange({ departureStagingDwellMinutes: value })
              }
              onQuickTurnThresholdMinutesChange={(value) =>
                onConfigChange({ quickTurnThresholdMinutes: value })
              }
              size="small"
              layout="column"
              disabledFields={[]}
              disabledSortingOptions={[
                "arrival",
                "longest-dwell",
                "latest-departure",
                "hybrid",
              ]}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('screens.ramp.autoPark.dialog.buttons.cancel')}</Button>
        <Button
          onClick={onRunAutoPark}
          variant="contained"
          color="primary"
          startIcon={<PlayIcon />}
          disabled={isScheduledView && (tripCount === 0 || (selectedTripIds?.size || 0) === 0)}
        >
          {t('screens.ramp.autoPark.dialog.buttons.run')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
